import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { focusAPI, projectAPI, reviewAPI, analyticsAPI, taskAPI } from '../services/api';
import { useLabDemo } from '../context/LabDemoContext';
import { 
  Target, Coffee, Sun, Moon, Zap, ClipboardCheck, 
  RefreshCw, Sparkles, Command, Activity, FlaskConical, Shield, PanelRightOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { DESIGN } from '../components/DesignSystem';
import { LabOriginBadge, LabConversionBanner } from '../components/LabBadges';
import ChiefOfStaffCommandSuite from '../components/ChiefOfStaffCommandSuite';
import { OneOnOnePrepModal } from '../components/OneOnOnePrep';

// Import sub-components
import {
  ScheduleMeetingModal,
  DelegateTaskModal,
  SendMessageModal,
  FocusItemCard,
  TimeBlockHeader,
  DailySummary,
  IntelligencePanel,
  QuickActionsPanel,
  QuickTeamPanel,
  BurnoutDetailPanel,
  BurnoutRiskSection,
  OrgBurnoutHeatMap,
  ReviewDecisionModal,
  ReassignReviewerModal,
  ExecutiveReviewSection,
  DemoRoleSwitcher,
  DEMO_ROLES,
  UNIQUE_IP_COLORS,
  CHIEF_OF_STAFF_COLORS,
  getRoleData
} from '../components/focus';

// ================================================
// MAIN FOCUS PAGE - COMMAND CENTER
// ================================================
const Focus = () => {
  const navigate = useNavigate();
  const { labFocusBlocks, demoCompleted, conversionDate } = useLabDemo();
  
  // Demo role switcher state
  const [activeRole, setActiveRole] = useState('ceo');
  const [demoData, setDemoData] = useState(null);
  
  // Chief of Staff Command Suite state
  const [showCommandSuite, setShowCommandSuite] = useState(false);
  
  // Core state
  const [data, setData] = useState(null);
  const [focusItems, setFocusItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  
  // Delegation data
  const [teamForDelegation, setTeamForDelegation] = useState([]);
  const [projectsForTask, setProjectsForTask] = useState([]);
  
  // Burnout states
  const [burnoutAlerts, setBurnoutAlerts] = useState([]);
  const [burnoutLoading, setBurnoutLoading] = useState(true);
  const [burnoutHeatmap, setBurnoutHeatmap] = useState([]);
  const [heatmapLoading, setHeatmapLoading] = useState(true);
  const [selectedBurnoutMember, setSelectedBurnoutMember] = useState(null);
  
  // Executive Review states
  const [reviewProjects, setReviewProjects] = useState([]);
  const [reviewSlaSummary, setReviewSlaSummary] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [eligibleReviewers, setEligibleReviewers] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedReviewProject, setSelectedReviewProject] = useState(null);
  
  // 1:1 Prep Modal state
  const [prepMember, setPrepMember] = useState(null);

  // ================================
  // Data Fetching
  // ================================
  const fetchFocusData = useCallback(async () => {
    try {
      const response = await focusAPI.getCommandCenter();
      setData(response.data);
      setFocusItems(response.data.focus_items || []);
      
      // Set delegation data
      if (response.data.quick_team) {
        setTeamForDelegation(response.data.quick_team.map(m => ({
          id: m.id,
          name: m.name,
          avatar: m.avatar,
          title: m.title,
          active_tasks: m.active_tasks,
          available: m.availability_status === 'available'
        })));
      }
    } catch (error) {
      console.error('Error fetching focus data:', error);
      toast.error('Failed to load focus data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBurnoutData = useCallback(async () => {
    try {
      setBurnoutLoading(true);
      const response = await analyticsAPI.getBurnoutAlerts();
      setBurnoutAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching burnout data:', error);
    } finally {
      setBurnoutLoading(false);
    }
  }, []);

  const fetchHeatmapData = useCallback(async () => {
    try {
      setHeatmapLoading(true);
      const response = await analyticsAPI.getBurnoutHeatmap();
      setBurnoutHeatmap(response.data.heatmap || []);
    } catch (error) {
      console.error('Error fetching heatmap:', error);
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  const fetchReviewData = useCallback(async () => {
    try {
      setReviewLoading(true);
      const [projectsRes, reviewersRes] = await Promise.all([
        reviewAPI.getAwaitingReview().catch(() => ({ data: { projects: [], sla_summary: null } })),
        reviewAPI.getEligibleReviewers().catch(() => ({ data: { reviewers: [] } }))
      ]);
      setReviewProjects(projectsRes.data.projects || []);
      setReviewSlaSummary(projectsRes.data.sla_summary);
      setEligibleReviewers(reviewersRes.data.reviewers || []);
    } catch (error) {
      console.error('Error fetching review data:', error);
    } finally {
      setReviewLoading(false);
    }
  }, []);

  const fetchProjectsForTask = useCallback(async () => {
    try {
      const response = await projectAPI.getAll();
      setProjectsForTask(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  useEffect(() => {
    fetchFocusData();
    fetchBurnoutData();
    fetchHeatmapData();
    fetchReviewData();
    fetchProjectsForTask();
  }, [fetchFocusData, fetchBurnoutData, fetchHeatmapData, fetchReviewData, fetchProjectsForTask]);

  // ================================
  // Role Switching Handler
  // ================================
  const handleRoleChange = useCallback((newRole) => {
    setActiveRole(newRole);
    const roleData = getRoleData(newRole);
    
    if (roleData) {
      // Use demo data for non-CEO roles
      setDemoData(roleData);
      setFocusItems(roleData.focus_items || []);
      setBurnoutAlerts(roleData.burnout_alerts || []);
    } else {
      // CEO role uses actual API data
      setDemoData(null);
      if (data) {
        setFocusItems(data.focus_items || []);
      }
      // Refresh real data
      fetchBurnoutData();
    }
  }, [data, fetchBurnoutData]);

  // ================================
  // Handlers
  // ================================
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchFocusData(),
      fetchBurnoutData(),
      fetchHeatmapData(),
      fetchReviewData()
    ]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const toggleItem = useCallback((itemId) => {
    setFocusItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, completed: !item.completed } : item
    ));
    
    const item = focusItems.find(i => i.id === itemId);
    if (item) {
      toast.success(item.completed ? 'Item unmarked' : 'Item completed!');
    }
  }, [focusItems]);

  const handleItemAction = useCallback((action, item) => {
    switch (action) {
      case 'start_call':
        toast.info(`Starting call for: ${item.title}`);
        break;
      case 'flag':
        toast.success(`Flagged: ${item.title}`);
        break;
      default:
        break;
    }
  }, []);

  const handleQuickAction = useCallback((actionKey, path) => {
    switch (actionKey) {
      case 'schedule':
        setShowMeetingModal(true);
        break;
      case 'delegate':
        setShowDelegateModal(true);
        break;
      case 'message':
        setShowMessageModal(true);
        break;
      case 'insights':
        navigate(path || '/insights');
        break;
      default:
        break;
    }
  }, [navigate]);

  const handleAlertAction = useCallback((alert) => {
    toast.info(`Handling alert: ${alert.title}`);
  }, []);

  const handleRecommendationAction = useCallback((rec) => {
    toast.info(`Executing: ${rec.title}`);
  }, []);

  // Modal submit handlers
  const handleScheduleMeeting = async (meetingData) => {
    await focusAPI.scheduleMeeting(meetingData);
  };

  const handleDelegateTask = async (taskData) => {
    await taskAPI.create(taskData);
  };

  const handleSendMessage = async (messageData) => {
    await focusAPI.sendMessage(messageData);
  };

  // Burnout action handler
  const handleBurnoutAction = useCallback(async (member, action) => {
    toast.info(`Action: ${action.title} for ${member.member_name}`);
  }, []);

  // Review handlers
  const handleReviewApprove = useCallback((project) => {
    setSelectedReviewProject(project);
    setShowApprovalModal(true);
  }, []);

  const handleReviewRevisions = useCallback((project) => {
    setSelectedReviewProject(project);
    setShowRevisionModal(true);
  }, []);

  const handleReviewReassign = useCallback((project) => {
    setSelectedReviewProject(project);
    setShowReassignModal(true);
  }, []);

  const handleViewProject = useCallback((project) => {
    navigate(`/projects/${project.id}`);
  }, [navigate]);

  const handleReviewDecision = async (decisionData) => {
    await reviewAPI.submitDecision(decisionData);
    await fetchReviewData();
  };

  const handleReassignReviewer = async (reassignData) => {
    await reviewAPI.reassignReviewer(reassignData);
    await fetchReviewData();
  };

  // ================================
  // Computed Values
  // ================================
  const { morningItems, middayItems, afternoonItems } = useMemo(() => {
    const parseTime = (timeStr) => {
      if (!timeStr) return 12;
      const match = timeStr.match(/(\d+):?(\d*)\s*(AM|PM)?/i);
      if (!match) return 12;
      let hours = parseInt(match[1]);
      const period = match[3]?.toUpperCase();
      if (period === 'PM' && hours < 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return hours;
    };

    const morning = focusItems.filter(item => {
      const hour = parseTime(item.time);
      return hour >= 5 && hour < 12;
    });

    const midday = focusItems.filter(item => {
      const hour = parseTime(item.time);
      return hour >= 12 && hour < 14;
    });

    const afternoon = focusItems.filter(item => {
      const hour = parseTime(item.time);
      return hour >= 14 || hour < 5;
    });

    return { morningItems: morning, middayItems: midday, afternoonItems: afternoon };
  }, [focusItems]);

  // ================================
  // Loading State
  // ================================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]" data-testid="focus-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div 
              className="w-14 h-14 rounded-full border-4 border-t-transparent animate-spin"
              style={{ borderColor: `${DESIGN.colors.primary}30`, borderTopColor: DESIGN.colors.primary }}
            />
            <div 
              className="absolute inset-0 w-14 h-14 rounded-full"
              style={{ boxShadow: `0 0 30px ${DESIGN.colors.primary}40` }}
            />
          </div>
          <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>
            Loading Command Center...
          </p>
        </div>
      </div>
    );
  }

  // ================================
  // Computed - Current theme colors based on role
  // ================================
  const isUniqueIP = activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations';
  const isChiefOfStaff = activeRole === 'chief_of_staff';
  const accentColor = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primary 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primary 
      : DESIGN.colors.primary;
  const accentDark = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primaryDark 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primaryDark 
      : DESIGN.colors.primaryDark;
  const accentSubtle = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primarySubtle 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primarySubtle 
      : DESIGN.colors.primarySubtle;
  const accentGlow = isChiefOfStaff 
    ? `0 0 20px ${CHIEF_OF_STAFF_COLORS.primaryGlow}` 
    : isUniqueIP 
      ? `0 0 20px ${UNIQUE_IP_COLORS.primaryGlow}` 
      : DESIGN.shadows.glowPrimary;

  // ================================
  // Render
  // ================================
  return (
    <div className="space-y-8" data-testid="focus-page">
      {/* Premium Page Header */}
      <div className="relative">
        {/* Animated gradient background */}
        <div 
          className="absolute inset-0 rounded-3xl opacity-40"
          style={{
            background: isChiefOfStaff
              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(147, 51, 234, 0.05) 50%, rgba(124, 58, 237, 0.05) 100%)'
              : isUniqueIP 
                ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(234, 179, 8, 0.05) 50%, rgba(251, 146, 60, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(0, 201, 167, 0.08) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(59, 130, 246, 0.05) 100%)',
            animation: 'gradientPulse 6s ease-in-out infinite'
          }}
        />
        
        <div className="relative flex items-center justify-between py-6">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${accentColor} 0%, ${accentDark} 100%)`,
                boxShadow: accentGlow
              }}
            >
              <Command className="w-7 h-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 
                  className="text-3xl font-black tracking-tight"
                  style={{ color: DESIGN.colors.textPrimary }}
                >
                  COMMAND CENTER
                </h1>
                {isChiefOfStaff && (
                  <div 
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                    style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primarySubtle }}
                  >
                    <div 
                      className="w-5 h-5 rounded flex items-center justify-center"
                      style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary }}
                    >
                      <Shield className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-bold" style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>
                      Executive Coordination
                    </span>
                  </div>
                )}
                {isUniqueIP && (
                  <div 
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                    style={{ backgroundColor: UNIQUE_IP_COLORS.primarySubtle }}
                  >
                    <div 
                      className="w-5 h-5 rounded flex items-center justify-center"
                      style={{ backgroundColor: UNIQUE_IP_COLORS.primary }}
                    >
                      <span className="text-[8px] font-black text-white">SF</span>
                    </div>
                    <span className="text-xs font-bold" style={{ color: UNIQUE_IP_COLORS.primary }}>
                      Soul Food
                    </span>
                  </div>
                )}
              </div>
              <p className="text-base" style={{ color: DESIGN.colors.textSecondary }}>
                {isChiefOfStaff
                  ? 'The control room for organizational coordination'
                  : isUniqueIP 
                    ? 'Your creative brand operating system' 
                    : 'Your AI-powered daily executive briefing'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isChiefOfStaff && (
              <button
                onClick={() => setShowCommandSuite(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all hover:shadow-lg hover:-translate-y-0.5"
                style={{ 
                  background: `linear-gradient(135deg, ${CHIEF_OF_STAFF_COLORS.primary} 0%, ${CHIEF_OF_STAFF_COLORS.primaryDark} 100%)`,
                  boxShadow: `0 0 20px ${CHIEF_OF_STAFF_COLORS.primaryGlow}`
                }}
                data-testid="open-command-suite-button"
              >
                <PanelRightOpen className="w-5 h-5 text-white" />
                <span className="text-sm font-bold text-white">
                  Command Suite
                </span>
              </button>
            )}
            <div 
              className="flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{ backgroundColor: accentSubtle }}
            >
              <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
              <span className="text-sm font-semibold" style={{ color: accentColor }}>
                AI Enhanced
              </span>
            </div>
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-3 rounded-xl transition-all hover:shadow-lg hover:-translate-y-0.5"
              style={{ 
                backgroundColor: DESIGN.colors.card,
                boxShadow: DESIGN.shadows.md
              }}
              data-testid="refresh-button"
            >
              <RefreshCw 
                className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                style={{ color: DESIGN.colors.textMuted }} 
              />
            </button>
          </div>
        </div>
      </div>

      {/* Demo Role Switcher */}
      <DemoRoleSwitcher 
        activeRole={activeRole} 
        onRoleChange={handleRoleChange}
      />

      {/* Main Content Grid */}
      <div 
        className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        style={(activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations') ? {
          '--accent-color': UNIQUE_IP_COLORS.primary,
          '--accent-subtle': UNIQUE_IP_COLORS.primarySubtle,
          '--accent-glow': UNIQUE_IP_COLORS.primaryGlow
        } : {}}
      >
        {/* Main Column - Focus Items */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Morning Block */}
          <TimeBlockHeader 
            label="Morning" 
            icon={Sun}
            accentColor={(activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations') ? UNIQUE_IP_COLORS.primary : undefined}
          />
          <div className="space-y-4">
            {morningItems.length > 0 ? morningItems.map(item => (
              <FocusItemCard 
                key={item.id} 
                item={item} 
                onToggle={toggleItem}
                onAction={handleItemAction}
                isUniqueIP={activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations'}
                onGenerateBrief={(participant) => setPrepMember(participant)}
              />
            )) : (
              <div 
                className="text-center py-8 rounded-2xl"
                style={{ backgroundColor: DESIGN.colors.background }}
              >
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>No morning items</p>
              </div>
            )}
          </div>

          {/* Midday Block */}
          <TimeBlockHeader 
            label="Midday" 
            icon={Coffee}
            accentColor={(activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations') ? UNIQUE_IP_COLORS.primary : undefined}
          />
          <div className="space-y-4">
            {middayItems.length > 0 ? middayItems.map(item => (
              <FocusItemCard 
                key={item.id} 
                item={item} 
                onToggle={toggleItem}
                onAction={handleItemAction}
                isUniqueIP={activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations'}
                onGenerateBrief={(participant) => setPrepMember(participant)}
              />
            )) : (
              <div 
                className="text-center py-8 rounded-2xl"
                style={{ backgroundColor: DESIGN.colors.background }}
              >
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>No midday items</p>
              </div>
            )}
          </div>

          {/* Afternoon Block */}
          <TimeBlockHeader 
            label="Afternoon" 
            icon={Moon}
            accentColor={(activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations') ? UNIQUE_IP_COLORS.primary : undefined}
          />
          <div className="space-y-4">
            {afternoonItems.length > 0 ? afternoonItems.map(item => (
              <FocusItemCard 
                key={item.id} 
                item={item} 
                onToggle={toggleItem}
                onAction={handleItemAction}
                isUniqueIP={activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations'}
                onGenerateBrief={(participant) => setPrepMember(participant)}
              />
            )) : (
              <div 
                className="text-center py-8 rounded-2xl"
                style={{ backgroundColor: DESIGN.colors.background }}
              >
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>No afternoon items</p>
              </div>
            )}
          </div>
          
          {/* LAB Created Focus Blocks Section */}
          {demoCompleted && labFocusBlocks && labFocusBlocks.length > 0 && (
            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, #06B6D4 100%)`,
                      boxShadow: `0 0 20px ${DESIGN.colors.primary}30`
                    }}
                  >
                    <FlaskConical className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      LAB Created Focus Blocks
                    </h3>
                    <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                      From Q3 Growth Strategy Session • {conversionDate}
                    </p>
                  </div>
                </div>
                <LabOriginBadge size="sm" />
              </div>
              
              <div className="space-y-3">
                {labFocusBlocks.map((block, idx) => (
                  <div 
                    key={block.id}
                    className="p-4 rounded-xl transition-all hover:shadow-lg"
                    style={{ 
                      backgroundColor: DESIGN.colors.card,
                      borderLeft: `4px solid ${DESIGN.colors.primary}`,
                      boxShadow: DESIGN.shadows.sm
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: `${DESIGN.colors.primary}15` }}
                        >
                          <Target className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
                            {block.title}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                              {block.duration}
                            </span>
                            <span 
                              className="text-xs px-2 py-0.5 rounded"
                              style={{ 
                                backgroundColor: block.priority === 'critical' 
                                  ? DESIGN.colors.errorSubtle 
                                  : block.priority === 'high'
                                    ? DESIGN.colors.amberSubtle
                                    : DESIGN.colors.background,
                                color: block.priority === 'critical' 
                                  ? DESIGN.colors.error 
                                  : block.priority === 'high'
                                    ? DESIGN.colors.amber
                                    : DESIGN.colors.textMuted
                              }}
                            >
                              {block.priority}
                            </span>
                            <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                              {block.type}
                            </span>
                          </div>
                          <p className="text-xs mt-2" style={{ color: DESIGN.colors.textSecondary }}>
                            Scheduled: {block.scheduledFor}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Executive Review Section - hidden for team members and operations roles */}
          {activeRole !== 'team_member' && activeRole !== 'unique_ip_operations' && (
            <div className="mt-10">
              <TimeBlockHeader 
                label="Executive Reviews" 
                icon={ClipboardCheck}
                accentColor={(activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations') ? UNIQUE_IP_COLORS.primary : undefined}
              />
              <ExecutiveReviewSection
                projects={demoData?.executive_reviews || reviewProjects}
                slaSummary={activeRole === 'ceo' ? reviewSlaSummary : null}
                onApprove={handleReviewApprove}
                onRequestRevisions={handleReviewRevisions}
                onReassign={handleReviewReassign}
                onViewProject={handleViewProject}
                onRefresh={fetchReviewData}
                loading={activeRole === 'ceo' ? reviewLoading : false}
                isUniqueIP={activeRole === 'unique_ip_director'}
              />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Daily Summary - use demo data or API data */}
          {(demoData?.daily_summary || data?.daily_summary) && (
            <DailySummary 
              summary={demoData?.daily_summary || data.daily_summary} 
              focusItems={focusItems}
              isUniqueIP={activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations'}
              isTeamMember={activeRole === 'team_member' || activeRole === 'unique_ip_operations'}
            />
          )}
          
          {/* Burnout Risk Alerts Section - hidden for team members and operations roles */}
          {activeRole !== 'team_member' && activeRole !== 'unique_ip_operations' && (
            <BurnoutRiskSection 
              alerts={demoData?.burnout_alerts || burnoutAlerts}
              loading={activeRole === 'ceo' ? burnoutLoading : false}
              onSelectMember={setSelectedBurnoutMember}
              onAction={handleBurnoutAction}
              isUniqueIP={activeRole === 'unique_ip_director'}
            />
          )}
          
          {/* Organization Burnout Heat Map - only for CEO and National Director */}
          {(activeRole === 'ceo' || activeRole === 'national_director') && (
            <OrgBurnoutHeatMap 
              heatmap={burnoutHeatmap}
              loading={heatmapLoading}
            />
          )}
          
          <IntelligencePanel 
            alerts={demoData?.alerts || data?.alerts || []}
            recommendations={demoData?.priority_recommendations || data?.priority_recommendations || []}
            onAlertAction={handleAlertAction}
            onRecommendationAction={handleRecommendationAction}
            isUniqueIP={activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations'}
          />
          
          <QuickActionsPanel 
            onAction={handleQuickAction}
            customActions={demoData?.quick_actions}
            isUniqueIP={activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations'}
          />
          
          {/* Quick Team / My Team panel */}
          {(demoData?.quick_team || data?.quick_team)?.length > 0 && (
            <QuickTeamPanel 
              team={demoData?.quick_team || data.quick_team}
              onMemberClick={(member) => toast.info(`Viewing ${member.name}'s profile`)}
              title={(activeRole === 'team_member' || activeRole === 'unique_ip_operations') ? 'My Team' : undefined}
              isUniqueIP={activeRole === 'unique_ip_director' || activeRole === 'unique_ip_operations'}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <ScheduleMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        team={teamForDelegation}
        onSubmit={handleScheduleMeeting}
      />
      
      <DelegateTaskModal
        isOpen={showDelegateModal}
        onClose={() => setShowDelegateModal(false)}
        team={teamForDelegation}
        projects={projectsForTask}
        onSubmit={handleDelegateTask}
      />
      
      <SendMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        team={teamForDelegation}
        onSubmit={handleSendMessage}
      />
      
      {/* Executive Review Modals */}
      <ReviewDecisionModal
        isOpen={showApprovalModal}
        onClose={() => { setShowApprovalModal(false); setSelectedReviewProject(null); }}
        project={selectedReviewProject}
        onSubmit={handleReviewDecision}
        type="approved"
      />
      
      <ReviewDecisionModal
        isOpen={showRevisionModal}
        onClose={() => { setShowRevisionModal(false); setSelectedReviewProject(null); }}
        project={selectedReviewProject}
        onSubmit={handleReviewDecision}
        type="revisions_requested"
      />
      
      <ReassignReviewerModal
        isOpen={showReassignModal}
        onClose={() => { setShowReassignModal(false); setSelectedReviewProject(null); }}
        project={selectedReviewProject}
        reviewers={eligibleReviewers}
        onSubmit={handleReassignReviewer}
      />
      
      {/* Burnout Detail Panel */}
      {selectedBurnoutMember && (
        <BurnoutDetailPanel
          member={selectedBurnoutMember}
          onClose={() => setSelectedBurnoutMember(null)}
          onAction={handleBurnoutAction}
        />
      )}

      {/* Chief of Staff Command Suite */}
      {isChiefOfStaff && (
        <ChiefOfStaffCommandSuite 
          isOpen={showCommandSuite} 
          onClose={() => setShowCommandSuite(false)} 
        />
      )}
      
      {/* 1:1 Prep Modal */}
      <OneOnOnePrepModal
        isOpen={!!prepMember}
        onClose={() => setPrepMember(null)}
        member={prepMember}
      />

      {/* Global Animations */}
      <style>{`
        @keyframes gradientPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Focus;
